import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { applyCorrection, previewCorrection } from '../api/dataCorrectionApi'
import { classifyFailure } from '../model/apiFailure'
import { canApply } from '../model/applyGate'
import {
  adoptSuggested, canAdjust, canPreview, commentRules, createDraft, intentKey, isNoChange, showsResumeAt, toIntent, validate, validateTime,
  windowOf, withComment, withDraggedInstant, withKind, withReason, withRecordedEnd, withResumeAt, withTapes, withTypedInstant
} from '../model/correctionDraft'
import { describeWriteFailure } from '../model/messageCatalog'
import { makeNameResolvers, workerFullName } from '../model/names'
import { parseIsoInstant } from '../model/timeGrid'
import { RETRY_WAIT_MS, usePreview } from './usePreview'

const NO_FAILURE = {}
const NO_FIELD_FAILURE = { reason: {}, comment: {} }
const NEVER_ADOPTED = { done: false }

// What the apply answered tells what to do with the preview (RN70-RN73):
//   - the engine, Spring or the plan rejected it: the preview is no longer valid and has to be made again
//   - a 500 keeps everything (RN72); the reason and the comment of Sun's field errors keep the preview too (RN54, RN73)
const KEEPS_PREVIEW = ['DATA_CORRECTION_UNEXPECTED_ERROR']

/**
 * The state of the open panel: the draft, the preview and the apply of ONE intervention (RN42-RN74).
 *
 * `selected` is `{ sessionKey, block, intervention }` (block = a block of the view) or an empty object; a new `sessionKey` starts
 * a new draft. `inReading` says whether the current reading still has the intervention: the page keeps the panel of one that a
 * reading lost while it was being applied (A202-23), and then the panel is read-only. Everything that talks to Sun lives here;
 * the panel only draws it.
 *
 * Story 211: an open intervention works with the `nowMs` of the reading as its recorded end (RN20), so `intervention` (also what the
 * page reads to place the handles and the band) already carries it as `end`; and in «close now» mode (RN22) the apply needs the
 * confirmation of the panel (RN24, RN26) and sends `closeNowConfirmed` with no plan hash (RN27).
 */
export const useCorrection = ({
  selected, inReading = true, nowMs = 0, canRequest, reasons, reasonsLoaded, t_, format, lookupWorkerName, lookupDeviceName, onApplied
}) => {
  const active = Boolean(selected && selected.sessionKey)
  const sessionKey = active ? selected.sessionKey : ''
  const readIntervention = active ? selected.intervention : undefined
  const intervention = useMemo(() => (readIntervention ? withRecordedEnd(readIntervention, nowMs) : readIntervention), [readIntervention, nowMs])
  const raw = active ? selected.block.raw : undefined
  const presenceRows = useMemo(() => (raw ? raw.lanes.presence : []), [raw])

  // The draft belongs to the panel that created it: a different intervention starts from its defaults (RN42).
  // The confirmation of «close now» belongs to the panel too: unchecked at every opening (RN24).
  const [store, setStore] = useState({ key: '', draft: {}, closeNowConfirmed: false })
  let current = store
  if (active && store.key !== sessionKey) {
    current = { key: sessionKey, draft: createDraft(intervention), closeNowConfirmed: false }
    setStore(current)
  }
  const draft = current.draft
  const closeNowConfirmed = current.closeNowConfirmed

  const update = useCallback((change) => setStore((existing) => {
    if (existing.key !== sessionKey) return existing
    const next = change(existing.draft)
    return { ...existing, draft: next }
  }), [sessionKey])

  const names = useMemo(
    () => (raw ? makeNameResolvers({ block: raw, lookupWorkerName, lookupDeviceName }) : undefined),
    [raw, lookupWorkerName, lookupDeviceName]
  )
  const errors = active ? validate(draft, intervention, presenceRows, format) : []
  // The time field is in error only with a local error of its own (RN48), never because the recorded edge is off the grid (A202-17).
  const timeInError = active && validateTime(draft, intervention, format).length > 0
  const previewable = active && canRequest && canPreview(draft, intervention, presenceRows, format)
  const inReadingRef = useRef(inReading)
  inReadingRef.current = inReading
  const intent = useMemo(() => (previewable ? toIntent(draft, intervention, raw.device.id, presenceRows) : {}), [previewable, draft, intervention, raw, presenceRows])
  const key = previewable ? intentKey(intent) : ''

  // ---- the failure texts, with the draft as it is when the answer arrives ----
  const failureContext = () => ({
    t_,
    format,
    names,
    workerName: names.worker(intervention.worker.id),
    kind: draft.kind,
    tapes: draft.tapes,
    imputationRows: raw.lanes.imputations
  })
  const describeFailure = (failure) => describeWriteFailure(failure, failureContext())

  const preview = usePreview({
    active: active && canRequest,
    sessionKey,
    intent,
    intentKey: key,
    // No new rehearsal for an intervention the reading no longer has (A202-23).
    canPreview: previewable && inReading,
    open: active && intervention.open === true,
    previewFn: previewCorrection,
    describeFailure
  })
  const closeNowMode = active && preview.closeNowMode

  // ---- the tapes Sun suggests are adopted once, after the first correct preview, if the user has not touched them (RN49) ----
  const adoption = useRef(NEVER_ADOPTED)
  useEffect(() => { adoption.current = NEVER_ADOPTED }, [sessionKey])
  useEffect(() => {
    if (!active || !preview.vigent || adoption.current.done) return
    adoption.current = { done: true }
    const adopted = adoptSuggested(draft, preview.result.suggestedTapes, { first: true })
    if (adopted.adopted) {
      update(() => adopted.draft)
      preview.requestImmediate()
    }
  }, [active, preview.vigent, preview.result]) // eslint-disable-line react-hooks/exhaustive-deps

  // ---- apply ----
  const [applyState, setApplyState] = useState({ applying: false, failure: NO_FAILURE, field: NO_FIELD_FAILURE })
  const applyTimer = useRef(0)
  // The panel the answers of an apply may reach: the one that sent it, while it is still the open one (RN66). A new panel does
  // not inherit the apply of the previous one, so the request remembers its own session and compares it with this one.
  const applySession = useRef('')
  useEffect(() => {
    applySession.current = sessionKey
    setApplyState({ applying: false, failure: NO_FAILURE, field: NO_FIELD_FAILURE })
    return () => {
      applySession.current = ''
      clearTimeout(applyTimer.current)
    }
  }, [sessionKey])

  // A202-23: the apply ended and the reading no longer has the intervention: its note stays, nothing more can be done.
  const readOnly = active && !inReading && !applyState.applying
  const reason = reasons.find((candidate) => candidate.code === draft.reasonCode)
  const comment = commentRules(draft.comment || '', Boolean(reason && reason.requiresComment))
  const gate = readOnly
    ? { enabled: false, blockedBy: 'readOnly' }
    : canApply({
      canRequest,
      vigent: preview.vigent,
      reasonChosen: Boolean(reason),
      commentValid: comment.valid,
      applying: applyState.applying,
      reasonsLoaded,
      closeNowMode,
      closeNowConfirmed
    })

  const latestBody = useRef({})
  const sendApply = useCallback((body, allowRetry) => {
    const owner = sessionKey
    const stillOwner = () => owner !== '' && applySession.current === owner
    setApplyState({ applying: true, failure: NO_FAILURE, field: NO_FIELD_FAILURE })
    applyCorrection(body).then((result) => {
      if (!stillOwner()) return
      if (result === undefined) { // Api_Functions already redirected: nothing more to do (RN74)
        setApplyState({ applying: false, failure: NO_FAILURE, field: NO_FIELD_FAILURE })
        return
      }
      setApplyState({ applying: false, failure: NO_FAILURE, field: NO_FIELD_FAILURE })
      onApplied(result)
    }, (error) => {
      if (!stillOwner()) return
      const failure = classifyFailure(error)
      if (failure.kind === 'aborted') return
      if (failure.kind === 'contract' && failure.message === 'DATA_CORRECTION_RETRY' && allowRetry) {
        applyTimer.current = setTimeout(() => { if (stillOwner()) sendApply(body, false) }, RETRY_WAIT_MS)
        return
      }
      const message = describeFailure(failure)
      const onField = message.placement === 'reason' || message.placement === 'comment'
      setApplyState({
        applying: false,
        failure: onField ? NO_FAILURE : message,
        field: onField ? { ...NO_FIELD_FAILURE, [message.placement]: message } : NO_FIELD_FAILURE
      })
      if (failure.kind === 'contract' && failure.message === 'DATA_CORRECTION_PLAN_CHANGED') {
        preview.invalidate()
        // RN68 asks for a new preview at once, unless the reading no longer has the intervention (A202-23).
        if (inReadingRef.current) preview.requestImmediate()
      } else if (!onField && !KEEPS_PREVIEW.includes(failure.message) && failure.status !== 500) {
        preview.invalidate()
      }
    })
  }, [sessionKey, onApplied, preview]) // eslint-disable-line react-hooks/exhaustive-deps

  const apply = useCallback(() => {
    const common = { intent, reasonCode: draft.reasonCode, comment: comment.sent === '' ? null : comment.sent }
    // RN27: «close now» sends no plan hash (Sun closes with the `now` of that moment) and the confirmation; a normal apply is as in F2.
    const body = closeNowMode
      ? { ...common, planHash: null, closeNowConfirmed: true }
      : { ...common, planHash: preview.result.planHash }
    latestBody.current = body
    sendApply(body, true)
  }, [intent, draft.reasonCode, comment.sent, preview.result, closeNowMode, sendApply])

  const applyAgain = useCallback(() => sendApply(latestBody.current, false), [sendApply])

  // ---- what the timeline needs to draw: the band of the stretch and the handles (RN40, RN41, RN50) ----
  const noErrors = errors.length === 0
  const localWindow = active && noErrors ? windowOf(draft, intervention) : { startMs: Number.NaN, endMs: Number.NaN }
  const window = preview.vigent
    ? { startMs: parseIsoInstant(preview.result.window.start).getTime(), endMs: parseIsoInstant(preview.result.window.end).getTime() }
    : localWindow

  const adjustable = active && canAdjust(intervention)
  const handlesShown = adjustable && draft.kind !== 'VOID' && !readOnly

  const setCloseNowConfirmed = useCallback((confirmed) => setStore((existing) => (existing.key !== sessionKey ? existing : { ...existing, closeNowConfirmed: confirmed })), [sessionKey])
  const setKind = useCallback((kind) => update((existing) => withKind(existing, kind, intervention)), [update, intervention])
  const setTyped = useCallback((ms) => update((existing) => withTypedInstant(existing, ms, intervention)), [update, intervention])
  const setDragged = useCallback((kind, ms) => update((existing) => withDraggedInstant(existing, kind, ms, intervention)), [update, intervention])
  const setTapes = useCallback((tapes) => update((existing) => withTapes(existing, tapes, true)), [update])
  const setResumeAt = useCallback((ms) => update((existing) => withResumeAt(existing, ms)), [update])
  const setReason = useCallback((code) => { update((existing) => withReason(existing, code)); setApplyState((state) => ({ ...state, field: { ...state.field, reason: {} } })) }, [update])
  const setComment = useCallback((text) => { update((existing) => withComment(existing, text)); setApplyState((state) => ({ ...state, field: { ...state.field, comment: {} } })) }, [update])

  const correctOnlyPresence = useCallback(() => {
    update((existing) => withTapes(existing, ['PRESENCE'], true))
    preview.requestImmediate()
  }, [update, preview])

  // «Back at…» cannot outlive the field that shows it (RN51).
  useEffect(() => {
    if (active && draft.resumeAt !== 0 && !showsResumeAt(draft, intervention, presenceRows)) update((existing) => withResumeAt(existing, 0))
  }, [active, draft, intervention, presenceRows, update])

  return {
    active,
    deviceId: raw ? raw.device.id : 0,
    draft,
    intervention,
    errors,
    timeInError,
    noChange: active && errors.length === 0 && isNoChange(draft, intervention),
    resumeVisible: active && showsResumeAt(draft, intervention, presenceRows),
    adjustable,
    handlesShown,
    window,
    preview,
    closeNowMode,
    closeNowConfirmed,
    setCloseNowConfirmed,
    reason,
    comment,
    gate,
    applying: applyState.applying,
    readOnly,
    applyFailure: applyState.failure,
    fieldFailure: applyState.field,
    workerName: active ? workerFullName(raw.workers.find((worker) => worker.id === intervention.worker.id) || { name: `#${intervention.worker.id}` }) : '',
    names,
    setKind,
    setTyped,
    setDragged,
    setTapes,
    setResumeAt,
    setReason,
    setComment,
    correctOnlyPresence,
    apply,
    applyAgain
  }
}
